import { useState, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//components
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import ChipStatus from 'Components/ChipStatus';
import { Tooltip } from "@gpstrackit/react-controls";
import { Icon } from "@mui/material";

//Views
import RefreshFirmwares from 'Modules/manufacturers/.components/refreshFirmware';
import RefreshDevicesConfig from 'Modules/manufacturers/.components/refreshDevicesConfig';
import AssignFirmware from 'Modules/manufacturers/.components/firmwareTab/.components/assignFirmware';

//Helpers
import { getDateFormatted, openLink } from 'Core/data/Helpers';

//Permission
import { HasPermission } from 'Core/security';

//Actions
import {
    manufacturersRefreshFirmwareOpenModal,
    firmwareAssignOpenModal,
    manufacturersRefreshDevicesConfigOpenModal
} from 'Redux/actions';
import { isInteger } from "lodash";

const FirmwareTab = (props) => {
    const { height, fromReviewFirmwares } = props;
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const refreshFirmwareOpenModal = useSelector(state => state.refreshFirmwareRedux.openModal);
    const assignFirmwareReduxOpenModal = useSelector(state => state.assignFirmwareRedux.openModal);
    const refreshDevicesConfigOpenModal = useSelector(state => state.refreshDevicesConfigRedux.openModal);
    const user = useSelector(state => state.securityRedux.user);

    const [columns, setColumns] = useState([
        {
            key: "id",
            field: "id",
            title: "Id",
            show: true,
            locked: true,
            width: 50,
            sort: true,
            export: true,
            functionString: 'return row?.id',
            render: (item) => {
                return <span>{item?.id}</span>
            }
        },
        {
            key: "version",
            field: "version",
            title: messages['firmware_tab_version'],
            show: true,
            resize: true,
            width: 180,
            sort: true,
            export: true,
            functionString: 'return row?.version',
            render: (item) => {
                return <span>{item?.version}</span>
            }
        },
        {
            key: "hardwareVersion",
            field: "hardwareVersion",
            title: messages['firmware_tab_hardware_version'],
            show: true,
            resize: true,
            width: 180,
            sort: true,
            export: true,
            functionString: 'return row?.hardwareVersion',
            render: (item) => {
                return (
                    <div style={{ display: 'flex' }}>
                        <span>{item?.hardwareVersion}</span>
                        {
                            item?.fileUrl && item?.fileUrl.length > 0 &&
                            <div style={{ marginLeft: 5 }}>
                                <Tooltip title={messages['common_download']}>
                                    <Icon style={{ color: '#1664f5', cursor: 'pointer' }} onClick={(e) => {
                                        openLink(item?.fileUrl, "_blank");
                                    }}>file_download</Icon>
                                </Tooltip>
                            </div>}
                    </div>
                )
            }
        },
        {
            key: "isActive",
            field: "isActive",
            title: messages['firmware_tab_status'],
            show: true,
            resize: true,
            width: 150,
            export: true,
            functionString: 'return row?.isActive ? "Active" : "Deactivated"',
            render: (item) => {
                return (
                    <ChipStatus
                        label={item?.isActive ? messages['firmware_tab_active'] : messages['firmware_tab_deactivated']}
                        statusCode={item?.isActive ? 1 : 0}
                    />
                )
            }
        },
        {
            key: "isDefault",
            field: "isDefault",
            title: messages['firmware_tab_default'],
            show: true,
            resize: true,
            width: 150,
            sort: true,
            export: true,
            functionString: 'return row?.isDefault ? "Yes" : "No"',
            render: (item) => {
                return (
                    <ChipStatus
                        label={item?.isDefault ? messages['firmware_tab_yes'] : messages['firmware_tab_no']}
                        statusCode={item?.isDefault ? 1 : 0}
                    />
                )
            }
        },
        {
            key: "firmware_type",
            field: "build",
            title: messages['firmware_tab_firmware_type'],
            show: true,
            resize: true,
            width: 150,
            export: true,
            functionString: 'return row?.build',
            render: (item) => {
                return <span>{item?.build}</span>
            }
        },
        {
            key: "releaseTime",
            field: "releaseTime",
            title: messages['firmware_tab_release_time'],
            show: true,
            resize: true,
            width: 200,
            sort: true,
            export: true,
            functionString: 'return row?.releaseTime && (new Date(parseInt(`${row?.releaseTime}`, 10))).toISOString().slice(0, 19).replace("T", " ")',
            render: (item) => {
                return <span>{item?.releaseTime && getDateFormatted(item?.releaseTime, "long", "datetime")}</span>
            }
        },
        {
            key: "description",
            field: "description",
            title: messages['firmware_tab_description'],
            show: true,
            resize: true,
            width: 350,
            export: true,
            functionString: 'return row?.description',
            render: (item) => {
                let long = !fromReviewFirmwares ? item?.description.length > 200 : false;
                return item?.description &&
                    <div style={{ display: 'grid', marginTop: 4 }}>
                        {
                            long &&
                            <Tooltip title={item?.description}>
                                <div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                    {item?.description.substring(0, 200)}...
                                </div>
                            </Tooltip>
                        }
                        {
                            !long &&
                            <div style={{ whiteSpace: 'wrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                <span>{item?.description}</span>
                            </div>
                        }
                    </div>
            }
        },

    ])
    const searchFields = useMemo(() => ["version", "build", "hardwareVersion"], []);

    // ═══════════════════════════════════════════════════════════════════════════
    // DATAVIEW HANDLERS & CONFIGURATION
    // Extracted from JSX for readability
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Handle condition changes from search/filters
     */
    const handleChangeConditions = useCallback((tags, conditions, total, newColumns, rows, sort) => {
        let currentConditions = {};
        let currentTags = {};
        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
        } else {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
        }

        currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'version');

        let tagsById = currentTags.filter((item) => typeof item.id === "number");
        if (tagsById?.length > 0) {
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }
        let tagsByLabel = currentTags.filter(item => typeof item.id === "string");
        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => (item.version));
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" });
            }
        }

        if (!conditions && tags) {
            tableRef?.current?.getData({ newConditions: currentConditions });
        } else {
            tableRef?.current?.getData({ newRows: rows, newSort: sort, newConditions: currentConditions, newColumns });
        }
    }, [searchFields]);

    /**
     * Handle column changes (reorder, resize, visibility)
     */
    const handleChangeColumns = useCallback((items) => setColumns(items), []);

    /**
     * Context menu options for row actions
     */
    const menuOptions = useMemo(() => !fromReviewFirmwares ? [
        {
            id: "refresh-firmwares",
            label: messages['firmware_tab_label'],
            show: true,
            disabled: !HasPermission(user, "manrf", "u"),
            action: () => {
                dispatch(manufacturersRefreshFirmwareOpenModal());
            }
        },
        {
            id: "assign-firmware-to",
            label: messages['firmware_tab_assign_firmware_to'],
            show: true,
            disabled: !HasPermission(user, "untaf", "u"),
            single: true,
            action: (items) => {
                dispatch(firmwareAssignOpenModal(items[0].id));
            }
        },
        {
            id: "update-vifleet-devices",
            label: messages['firmware_tab_update_vidfleet_devices'],
            show: true,
            disabled: !HasPermission(user, "manrf", "u"),
            action: () => {
                dispatch(manufacturersRefreshDevicesConfigOpenModal());
            }
        },
    ] : [], [fromReviewFirmwares, messages, user, dispatch]);

    const graphqlConfig = useMemo(() => ({
        function: "searchFirmwares",
        sortType: "FirmwareSortInput",
        conditionsType: "FirmwareSearchInput",
        customQuery: "id firmwareId fileUrl",
        dynamicQuery: true,
    }), []);

    const searchConfig = useMemo(() => ({
        fields: ["version", "build", "hardwareVersion"],
        optionValue: "id",
        optionLabel: "version",
    }), []);

    const uiConfig = useMemo(() => ({
        hideSplit: true,
        hideFilters: true,
        hidePagination: true,
        height,
    }), [height]);

    const exportConfig = useMemo(() => ({
        fileName: "firmwares",
    }), []);

    const defaultsConfig = useMemo(() => ({
        sort: { field: "id", order: "DESC" },
    }), []);

    const selectionConfig = useMemo(() => ({
        disabled: fromReviewFirmwares,
    }), [fromReviewFirmwares]);

    return (
        <div style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                name={`pm-firmwares-tab${fromReviewFirmwares ? "-review" : ""}`}
                entity={`firmwares-tab${fromReviewFirmwares ? "-review" : ""}`}
                columns={columns}
                infiniteScroll
                graphqlConfig={graphqlConfig}
                searchConfig={searchConfig}
                uiConfig={uiConfig}
                exportConfig={exportConfig}
                defaultsConfig={defaultsConfig}
                selectionConfig={selectionConfig}
                onChangeConditions={handleChangeConditions}
                onChangeColumns={handleChangeColumns}
                tableButtons={["export", "orderColumn"]}
                options={menuOptions}
            />
            {refreshFirmwareOpenModal && <RefreshFirmwares tableRef={tableRef} />}
            {assignFirmwareReduxOpenModal && <AssignFirmware tableRef={tableRef} />}
            {refreshDevicesConfigOpenModal && <RefreshDevicesConfig tableRef={tableRef} />}
        </div>
    )
}

export default FirmwareTab